import { initializeApp } from "firebase/app";
import { getAnalytics, isSupported as isAnalyticsSupported } from "firebase/analytics";
import { getAuth, setPersistence, browserLocalPersistence, type Auth } from "firebase/auth";
import { 
  getFirestore, 
  enableIndexedDbPersistence,
  enableMultiTabIndexedDbPersistence,
  type Firestore 
} from 'firebase/firestore';
import { getStorage, type FirebaseStorage } from 'firebase/storage';

// Use environment variables for security - DO NOT hardcode credentials
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  databaseURL: import.meta.env.VITE_FIREBASE_DATABASE_URL,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Validate required config
if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
  console.error('❌ Firebase configuration is incomplete. Check your .env file.');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);
console.log('✓ Firebase app initialized');

// Initialize Analytics only if supported and measurement ID is available
let analytics: any = null;
if (firebaseConfig.measurementId) {
  isAnalyticsSupported()
    .then((supported) => {
      if (supported) {
        analytics = getAnalytics(app);
        console.log('✓ Firebase analytics initialized');
      } else {
        console.log('ℹ Analytics not supported in this environment');
      }
    })
    .catch((error) => {
      console.warn('⚠ Analytics initialization failed:', error.message);
    });
} else {
  console.log('ℹ Analytics measurement ID not configured');
}

// Initialize Auth
const auth: Auth = getAuth(app);
console.log('✓ Firebase auth initialized');

// Early console.warn override to suppress the specific deprecation message
// from Firebase SDK (logged before our later override below takes effect)
const __origWarn = console.warn;
console.warn = function(...args: any[]) {
  const msg = String(args[0] || '');
  if (msg.includes('enableMultiTabIndexedDbPersistence() will be deprecated')) {
    return; // swallow immediately
  }
  __origWarn.apply(console, args);
};

// Initialize Firestore (simple, without custom settings to avoid conflicts)
const db: Firestore = getFirestore(app);
console.log('✓ Firestore initialized');

// Initialize Storage
const storage: FirebaseStorage = getStorage(app);
console.log('✓ Storage initialized');

// Enable Firestore offline persistence with multi-tab support
// Wrap in setTimeout to avoid blocking initialization
setTimeout(() => {
  enableMultiTabIndexedDbPersistence(db)
    .then(() => {
      console.log('✓ Firestore offline persistence enabled (multi-tab)');
    })
    .catch((err) => {
      if (err.code === 'failed-precondition') {
        // Multiple tabs open, try single tab persistence
        console.log('ℹ Multi-tab persistence unavailable, using single-tab mode');
        enableIndexedDbPersistence(db)
          .then(() => {
            console.log('✓ Firestore offline persistence enabled (single-tab)');
          })
          .catch((error) => {
            console.warn('⚠ Offline persistence disabled:', error.message);
          });
      } else if (err.code === 'unimplemented') {
        console.log('ℹ Offline persistence not supported in this browser');
      } else {
        console.warn('⚠ Offline persistence error:', err.message);
      }
    });
}, 100);

// Set persistence to LOCAL for session persistence
setPersistence(auth, browserLocalPersistence)
  .then(() => {
    console.log('✓ Firebase auth persistence set successfully');
  })
  .catch((error) => {
    console.warn('⚠ Auth persistence error:', error.message);
  });

// Complete suppression of unnecessary console warnings and errors
const originalConsoleWarn = console.warn;
const originalConsoleError = console.error;

console.warn = function(...args) {
  const message = String(args[0] || '');
  
  // Comprehensive filter for Firebase/Firestore warnings
  const suppressedPatterns = [
    'Failed to fetch',
    'remote config',
    'RemoteConfig',
    'CORS',
    'measurementId',
    'same origin policy',
    'Operation failed',
    'Retrying in',
    'client is offline',
    'Could not reach',
    'backend',
    'Firestore',
    'Detected an update time',
    'update time that is in the future',
    'timestamp',
    'clock skew',
    'enableMultiTabIndexedDbPersistence',
    'will be deprecated',
    'FirestoreSettings.cache',
    '@firebase/firestore'
  ];
  
  // Check if message contains ANY of the suppressed patterns
  if (suppressedPatterns.some(pattern => message.includes(pattern))) {
    return; // Completely silent
  }
  
  originalConsoleWarn.apply(console, args);
};

console.error = function(...args) {
  const message = String(args[0] || '');
  
  // Comprehensive filter for Firebase errors
  const suppressedPatterns = [
    'Failed to get document',
    'client is offline',
    'Could not reach',
    'Firestore',
    'backend',
    'enableMultiTabIndexedDbPersistence',
    'Operation failed',
    'attempt',
    'Retrying',
    'FirebaseError',
    'unavailable',
    'deadline-exceeded',
    'Detected an update time',
    'update time that is in the future',
    '@firebase/firestore'
  ];
  
  // Check if message contains ANY of the suppressed patterns
  if (suppressedPatterns.some(pattern => message.includes(pattern))) {
    return; // Completely silent
  }
  
  originalConsoleError.apply(console, args);
};

console.log('✓ Firebase initialization complete');

export { auth, db, storage, analytics };
export default app;
